import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetData } from "../../../Network/useGetQueryData";
import { useEffect, useState } from "react";
import { RequestSvg } from "../../../components/sideMenu/SidebarSVG/RequestSvg";
import { Button, Col, Dropdown, Image, message, Row } from "antd";
import "./style.scss";
import { Header } from "../../../components/Header";
import { BackArrow } from "../../../Icons/BackArrow";
import { Loading } from "../../../components/Loading";
import dayjs from "dayjs";
import { RequestServices } from "./RequestServices";
import { useSelector } from "react-redux";
import { statusMap } from "../../../utils/requestStatus";
import { DownOutlined } from "@ant-design/icons";
import { OtpConfirmation } from "./otpModal";
import { usePostData } from "../../../Network/usePostData";
import { ResultModal } from "../AddResult";
import apiClient from "../../../Network";
import { useTranslation } from "react-i18next";
import { UserRolesId } from "../../enum";
import { ReasonNoteModal } from "./ReasonModal";

export const ViewRequest = () => {
  const { t, i18n } = useTranslation();
  const { requestId } = useParams();
  const [requestData, setRequestData] = useState<any>();
  const [OtpOpened, setOtpOpened] = useState<boolean>();
  const [isResultOpened, setIsResultOpened] = useState<boolean>();
  const navigate = useNavigate();
  const [reasonModal, setReasonModal] = useState<{
    isOpen: boolean;
    url: string;
  }>();
  const userInfo = useSelector((state: any) => state.user.user) || {};

  const { data, refetch } = useGetData<{ data: any }>({
    queryKey: ["view-requests"],
    url: `service-request/${requestId}`,
    options: { enabled: !!requestId, queryKey: ["service-request"] },
  });

  const { data: results, refetch: refetchResults } = useGetData<{ data: any }>({
    queryKey: ["service-request-visit"],
    url: `service-request-visit-result?serviceRequestId=${requestId}&offset=0&limit=10&withDeleted=false`,
    options: { enabled: !!requestId, queryKey: ["service-request-visit"] },
  });

  const {
    status,
    customer,
    id,
    totalAmountAfterDiscount,
    createdAt,
    isPaid,
    children,
    paymentMethod,
    prescriptionUrl,
    customerId,
    cancellationNote,
    cancellationReason,
    rejectionNote,
    rejectionReason,
  } = requestData ?? {};

  const { user, code } = customer ?? {};
  const { firstName, lastName, phoneNumber, countryCode, email } = user ?? {};

  const { data: customerRequests, isLoading } = useGetData<{
    data: { items: any[]; count: number };
  }>({
    queryKey: [
      "requests",
      {
        offset: 0,
        limit: 3,
        createdAt: 1,
      },
    ],
    url: "/service-request",
    params: {
      offset: 0,
      limit: 3,
      withDeleted: false,
      sortKey: "createdAt:-1",
      customerId: customerId,
    },
  });

  const { mutateAsync: sendOtp, isPending: loadingOtp } = usePostData({
    url: "customer/send-otp",
    options: {
      onSuccess: () => setOtpOpened(true),
      onError: () => {
        message.error("Something went wrong");
      },
    },
  });

  const handleAction = async (url: string, body?: any) => {
    try {
      await apiClient.post(`service-request${url}`, body);
      refetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Can't Change Request Status",
      );
    }
  };

  const statusMenuList = [
    ...(status === "waitingAdminApproval" ||
    status === "waitingCustomerApproval"
      ? [
          {
            title: t("viewRequest.approve"),
            action: (id: string | number) =>
              setReasonModal({ isOpen: true, url: `/${id}/approve` }),
          },
          {
            title: t("viewRequest.reject"),
            action: (id: string | number) =>
              setReasonModal({ isOpen: true, url: `/${id}/reject` }),
          },
        ]
      : []),
    ...(status === "waitingPayment" && paymentMethod !== "cash"
      ? [
          {
            title: t("viewRequest.changePayment"),
            action: (id: string | number) =>
              handleAction(`/change-payment-method`, {
                serviceRequestId: id,
                paymentMethod: "cash",
              }),
          },
        ]
      : []),
    ...(status === "inProgress" || isPaid
      ? [
          {
            title: t("viewRequest.complete"),
            action: (id: string | number) =>
              setReasonModal({ isOpen: true, url: `/${id}/complete` }),
          },
        ]
      : []),
    {
      title: t("viewRequest.cancelRequest"),
      action: (id: string | number) =>
        setReasonModal({ isOpen: true, url: `/${id}/cancel` }),
    },
  ];

  const StatusChange = () => {
    const menuItems = statusMenuList.map((item, index) => ({
      key: index.toString(),
      label: <span onClick={() => item.action(id)}>{item.title}</span>,
    }));

    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        disabled={status === "completed"}
      >
        <span style={{ cursor: "pointer" }}>
          <p className={`status-header-text  ${status}`}>
            {statusMap(status)}
            {status !== "completed" && <DownOutlined />}
          </p>
        </span>
      </Dropdown>
    );
  };

  useEffect(() => {
    if (data?.data) setRequestData(data.data);
  }, [data]);

  const RequestInfo = ({
    title,
    details,
  }: {
    title: string;
    details: string;
  }) => (
    <div className="request-info-col">
      <p>{title}</p>
      <span>{details}</span>
    </div>
  );

  const totalSum = children?.reduce(
    (acc: any, item: any) => acc + item?.totalAmount,
    0,
  );
  const totalAfterDiscountSum = children?.reduce(
    (acc: any, item: any) => acc + item?.totalAmountAfterDiscount,
    0,
  );

  function handleConcatServices(record: any): string {
    const names: string[] = (record.children || []).reduce(
      (acc: string[], child: any) => {
        const svcNames = (child.services || [])
          .map((s: any) => s?.service?.name?.[i18n.language || "en"])
          .filter(Boolean) as string[];
        return acc.concat(svcNames);
      },
      [],
    );

    const unique = Array.from(new Set(names));
    return unique.join(", ");
  }

  const mappedChildren = children?.flatMap((item: any) =>
    item.services.map((serviceItem: any) => ({
      serviceProvider: item.serviceProvider,
      quantity: serviceItem.quantity,
      createdAt: serviceItem.createdAt,
      updatedAt: serviceItem.updatedAt,
      logTracker: serviceItem.logTracker,
      price: serviceItem.price,
      priceAfterDiscount: serviceItem.priceAfterDiscount,
      medHubPrice: serviceItem.medHubPrice,
      serviceRequestChildId: serviceItem.serviceRequestChildId,
      serviceId: serviceItem.serviceId,
      service: serviceItem.service,
    })),
  );

  if (!requestData) return <Loading />;

  return (
    <div className="view-request-wrapper">
      <Header
        title={t("viewRequest.headerTitle")}
        backIcon={
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/requests")}
          >
            <BackArrow />
          </span>
        }
      />

      <div className="view-request-header">
        <p>{t("viewRequest.viewRequestTitle")}</p>

        <div className="request-header-action">
          {status === "inProgress" &&
            userInfo?.roleId !== UserRolesId.Admin && (
              <Button
                type="primary"
                onClick={() =>
                  sendOtp({
                    customerId,
                    serviceProviderId: children?.[0]?.serviceProviderId,
                  })
                }
              >
                {t("viewRequest.takeServiceNow")}
              </Button>
            )}

          {status === "completed" &&
            !results?.data?.items?.filter(
              (i: any) => i.serviceRequestId === +requestId!!,
            )?.length && (
              <Button type="primary" onClick={() => setIsResultOpened(true)}>
                {t("viewRequest.addResult")}
              </Button>
            )}

          {userInfo?.roleId === UserRolesId.Admin &&
          status !== "cancelled" &&
          status !== "rejected" ? (
            StatusChange()
          ) : (
            <p className={`status-header-text  ${status}`}>
              {statusMap(status)}
            </p>
          )}
        </div>
      </div>

      <div className="requests-info-wrapper">
        <p className="request-header-title">
          <span>
            <RequestSvg />
          </span>
          {t("viewRequest.newServiceRequest")}
        </p>

        {(status === "cancelled" || status === "rejected") && (
          <div className="request-info-section">
            <p>
              {status === "rejected"
                ? t("requests.rejectReason")
                : t("requests.cancelReason")}
            </p>
            <div className="rejection-reason-modal">
              <p>
                {cancellationNote ||
                  cancellationReason ||
                  rejectionNote ||
                  rejectionReason}
              </p>
              <span></span>
            </div>
          </div>
        )}

        <div className="request-info-section">
          <p>{t("viewRequest.userDetails")}</p>
          <div className="request-info-data">
            <p>
              <span>
                {firstName?.charAt(0)}
                {lastName?.charAt(0)}
              </span>
              {firstName} {lastName}
            </p>
            <RequestInfo title={t("viewRequest.policyNumber")} details={code} />
            <RequestInfo
              title={t("viewRequest.mobile")}
              details={`${countryCode} ${phoneNumber}`}
            />
            <RequestInfo title={t("viewRequest.email")} details={email} />
          </div>
        </div>

        <div className="request-info-section">
          <p>{t("viewRequest.serviceDetails")}</p>
          <div className="request-info-data">
            <RequestInfo title={t("viewRequest.requestId")} details={id} />
            <RequestInfo
              title={t("viewRequest.requestedOn")}
              details={dayjs(createdAt).format("M/D/YYYY - h:mmA")}
            />
            <RequestInfo
              title={t("viewRequest.serviceCost")}
              details={totalAmountAfterDiscount}
            />
            <RequestInfo
              title={t("viewRequest.status")}
              details={statusMap(status)}
            />
            <RequestInfo
              title={t("viewRequest.paymentMethod")}
              details={paymentMethod}
            />
            <RequestInfo
              title={t("viewRequest.payment")}
              details={
                isPaid ? t("viewRequest.paid") : t("viewRequest.notPaid")
              }
            />
          </div>
        </div>

        <div className="request-info-section">
          <p>{t("viewRequest.previousRequests")}</p>
          <div className="request-info-data">
            {customerRequests?.data?.items?.map((item) => (
              <div className="request-info-col">
                <p>
                  {t("requests.requestId")}:{" "}
                  <a
                    style={{
                      color: "#215479",
                      fontWeight: "bold",
                      textDecoration: "none",
                    }}
                    href={`/request/${item?.id}`}
                    target="_blank"
                  >
                    {item?.id}
                  </a>
                </p>
                <span>{`${t("requests.services")}: ${handleConcatServices(item)}`}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="request-divider" />

        <Row gutter={16}>
          <Col lg={12} sm={24}>
            {children?.length > 0 ? (
              <>
                <RequestServices
                  results={results?.data?.items?.filter(
                    (i: any) => i.serviceRequestId === +requestId!!,
                  )}
                  services={mappedChildren}
                />
                <div className="request-divider service" />
                <div className="total-section">
                  <p>{t("viewRequest.total")}</p>
                  <span>{(totalSum || 0)?.toFixed(2)}</span>
                </div>
                <div className="total-section med-hub">
                  <p>{t("viewRequest.medhubShare")}</p>
                  <span>
                    {(totalSum - totalAfterDiscountSum || 0)?.toFixed(2)}
                  </span>
                </div>
                <div className="total-section client">
                  <p>{t("viewRequest.clientShare")}</p>
                  <span>{(totalAfterDiscountSum || 0)?.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="request-services-wrapper">
                <p>{t("viewRequest.serviceBreakdown")}</p>
                <span
                  style={{
                    height: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {t("viewRequest.noServicesYet")}
                  <Link
                    style={{ display: "inline-block", marginLeft: "5px" }}
                    to={`/add-request/${id}`}
                  >
                    {t("viewRequest.addServices")}
                  </Link>
                </span>
              </div>
            )}
          </Col>

          {prescriptionUrl && (
            <Col lg={12} sm={24}>
              <Image width={200} src={prescriptionUrl} />
            </Col>
          )}
        </Row>
      </div>

      {OtpOpened && (
        <OtpConfirmation
          otpData={{
            customerId,
            serviceProviderId: children?.[0]?.serviceProviderId,
          }}
          onResend={() =>
            sendOtp({
              customerId,
              serviceProviderId: children?.[0]?.serviceProviderId,
            })
          }
          refetch={refetch}
          paymentMethod={paymentMethod}
          onCloseModal={() => {
            setOtpOpened(false);
            refetch();
          }}
        />
      )}

      {isResultOpened && (
        <ResultModal
          services={mappedChildren}
          onCloseModal={() => {
            setIsResultOpened(false);
            refetchResults();
          }}
        />
      )}

      {reasonModal?.isOpen && (
        <ReasonNoteModal
          url={reasonModal.url}
          hasReason={
            reasonModal.url?.includes("cancel") ||
            reasonModal.url?.includes("reject")
          }
          onCloseModal={() => setReasonModal({ isOpen: false, url: "" })}
          refetch={refetch}
        />
      )}
    </div>
  );
};
